import React, { useState, useEffect, useRef } from 'react';
import './CryptoTicker.css';

const CryptoTicker = () => {
  const [cryptoData, setCryptoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currencies = [
    { 
      code: 'usd', 
      symbol: '$', 
      name: 'US Dollar', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/us.svg'
    },
    { 
      code: 'eur', 
      symbol: '€', 
      name: 'Euro', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/eu.svg'
    },
    { 
      code: 'gbp', 
      symbol: '£', 
      name: 'British Pound', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/gb.svg'
    },
    { 
      code: 'jpy', 
      symbol: '¥', 
      name: 'Japanese Yen', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/jp.svg'
    },
    { 
      code: 'aud', 
      symbol: 'A$', 
      name: 'Australian Dollar', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/au.svg'
    },
    { 
      code: 'cad', 
      symbol: 'C$', 
      name: 'Canadian Dollar', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/ca.svg'
    },
    { 
      code: 'chf', 
      symbol: 'Fr', 
      name: 'Swiss Franc', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/ch.svg'
    },
    { 
      code: 'cny', 
      symbol: '¥', 
      name: 'Chinese Yuan', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/cn.svg'
    },
    { 
      code: 'inr', 
      symbol: '₹', 
      name: 'Indian Rupee', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/in.svg'
    },
    { 
      code: 'krw', 
      symbol: '₩', 
      name: 'South Korean Won', 
      flag: 'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.5.0/flags/4x3/kr.svg'
    }
  ];

  const handleCurrencyChange = (code) => {
    setSelectedCurrency(code);
    setIsDropdownOpen(false);
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
  };

  useEffect(() => {
    const fetchData = async (retries = 3) => {
      try {
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=${selectedCurrency}&ids=bitcoin,ethereum,solana,cardano,ripple,chainlink,hedera&order=market_cap_desc&sparkline=false`,
          { 
            timeout: 8000,
            mode: 'cors',
            headers: {
              'Accept': 'application/json',
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache',
            }
          }
        );
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
        }
        
        const data = await response.json();
        console.log('API Response:', data);
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received');
        }
        
        const formattedData = data.map(coin => ({
          id: coin.id,
          symbol: coin.symbol.toUpperCase(),
          price: coin.current_price,
          change24h: coin.price_change_percentage_24h,
          image: coin.image
        }));
        
        setCryptoData(formattedData);
        setLoading(false);
        setError(null);
        setRetryCount(0);
      } catch (error) {
        console.error('Error fetching crypto data:', error);
        if (retries > 0) {
          console.log(`Retrying... ${retries} attempts left`);
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            fetchData(retries - 1);
          }, 5000);
        } else {
          setLoading(false);
          setError('Failed to fetch data. Click to retry.');
        }
      }
    };

    fetchData();
    const interval = setInterval(() => fetchData(), 60000);
    return () => clearInterval(interval);
  }, [selectedCurrency]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: selectedCurrency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const formatChange = (change) => {
    return change.toFixed(2);
  };

  if (loading) {
    return (
      <div className="crypto-ticker loading">
        {retryCount > 0 ? `Retrying... (Attempt ${retryCount}/3)` : 'Loading...'}
      </div>
    );
  }

  if (error) {
    return (
      <div className="crypto-ticker error" onClick={handleRetry}>
        Error: {error}
      </div>
    );
  }

  return (
    <div className="crypto-ticker">
      <div className="ticker-wrap">
        <div className="ticker-container">
          {/* First set of items */}
          {cryptoData.map((crypto) => (
            <div key={`first-${crypto.id}`} className="ticker-item">
              <span className="crypto-name">
                <img 
                  src={crypto.image} 
                  alt={`${crypto.id} icon`} 
                  className="crypto-icon"
                />
                {crypto.id.charAt(0).toUpperCase() + crypto.id.slice(1)} ({crypto.symbol})
              </span>
              <span className="crypto-price">{formatPrice(crypto.price)}</span>
              <span className={`crypto-change ${crypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                {crypto.change24h >= 0 ? '+' : ''}{formatChange(crypto.change24h)}%
              </span>
            </div>
          ))}
          {/* Second set of items */}
          {cryptoData.map((crypto) => (
            <div key={`second-${crypto.id}`} className="ticker-item">
              <span className="crypto-name">
                <img 
                  src={crypto.image} 
                  alt={`${crypto.id} icon`} 
                  className="crypto-icon"
                />
                {crypto.id.charAt(0).toUpperCase() + crypto.id.slice(1)} ({crypto.symbol})
              </span>
              <span className="crypto-price">{formatPrice(crypto.price)}</span>
              <span className={`crypto-change ${crypto.change24h >= 0 ? 'positive' : 'negative'}`}>
                {crypto.change24h >= 0 ? '+' : ''}{formatChange(crypto.change24h)}%
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="currency-dropdown" ref={dropdownRef}>
        <div 
          className="currency-dropdown-header" 
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <img 
            src={currencies.find(c => c.code === selectedCurrency).flag} 
            alt={`${selectedCurrency} flag`} 
            className="currency-flag"
          />
          <span>{currencies.find(c => c.code === selectedCurrency).symbol}</span>
          <span className="currency-name-header">
            {currencies.find(c => c.code === selectedCurrency).name}
          </span>
          <span className="dropdown-arrow">{isDropdownOpen ? '▲' : '▼'}</span>
        </div>
        {isDropdownOpen && (
          <div className="currency-dropdown-content">
            {currencies.map(currency => (
              <div
                key={currency.code}
                className={`currency-option ${selectedCurrency === currency.code ? 'selected' : ''}`}
                onClick={() => handleCurrencyChange(currency.code)}
              >
                <img 
                  src={currency.flag} 
                  alt={`${currency.name} flag`} 
                  className="currency-flag"
                />
                <span>{currency.symbol}</span>
                <span className="currency-name">{currency.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CryptoTicker; 